import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('No verification token provided.');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await api.post(`/auth/verify-email?token=${token}`);
      setStatus('success');
      setMessage(response.data.message || 'Your email has been verified successfully!');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Failed to verify email. The link may be invalid or expired.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/smarterhoops-logo.png" 
            alt="Smarter Hoops" 
            className="h-16 mx-auto mb-4"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <p className="text-slate-600 italic">Less Noise. More Wins. Better Players.</p>
        </div>

        {/* Verification Card */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-slate-200 p-8">
          {status === 'verifying' && (
            <div className="text-center" data-testid="verify-loading">
              <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold text-slate-900 uppercase mb-2">
                Verifying Email
              </h1>
              <p className="text-slate-600">Please wait while we verify your email address...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center" data-testid="verify-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 uppercase mb-2">
                Email Verified!
              </h1>
              <p className="text-slate-600 mb-6">{message}</p>
              <Button 
                onClick={() => navigate('/')}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase"
                data-testid="continue-to-login"
              >
                Continue
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center" data-testid="verify-error">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 uppercase mb-2">
                Verification Failed
              </h1>
              <p className="text-slate-600 mb-6">{message}</p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/')}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase"
                >
                  Continue
                </Button>
                <p className="text-sm text-slate-500">
                  Need a new verification link?{' '}
                  <a href="mailto:help@smarterhoops.com" className="text-blue-600 hover:text-blue-700 font-medium">
                    Email support
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Need assistance? Email us at{' '}
          <a href="mailto:help@smarterhoops.com" className="text-blue-600 hover:underline">
            help@smarterhoops.com
          </a>
        </p>
      </div>
    </div>
  );
}
